import { motion, useReducedMotion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";

export const IMG = {
  hero: "/self-booking/welcome_hero.jpg", vet: "/self-booking/category_veterinary.jpg",
  grooming: "/self-booking/category_grooming.jpg", boarding: "/self-booking/category_boarding.jpg",
  other: "/self-booking/category_other.jpg", success: "/self-booking/success_celebration.jpg",
};

export const DEPT = {
  vet: { label: "Veterinary", blurb: "Check-ups, vaccinations and gentle care from our vets." },
  grooming: { label: "Grooming", blurb: "Baths, haircuts and pampering to look and feel their best." },
  boarding: { label: "Boarding", blurb: "Cosy stays with round-the-clock love while you're away." },
  other: { label: "Other services", blurb: "Daycare, training and everything in between." },
};

export function Step({ children, k }) {
  const reduce = useReducedMotion();
  return (
    <motion.div key={k} initial={reduce ? { opacity: 0 } : { opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
      exit={reduce ? { opacity: 0 } : { opacity: 0, y: -12 }} transition={{ duration: 0.32, ease: "easeOut" }}>
      {children}
    </motion.div>
  );
}

export const Eyebrow = ({ children }) => (
  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--sb-lav)]">{children}</p>
);

export const Title = ({ children, sub, testid }) => (
  <div className="max-w-2xl">
    <h1 className="sb-serif text-4xl font-semibold leading-[1.05] sm:text-5xl" data-testid={testid}>{children}</h1>
    {sub && <p className="mt-3 text-base text-[var(--sb-muted)] sm:text-lg">{sub}</p>}
  </div>
);

export function Btn({ children, onClick, disabled, busy, variant = "primary", testid, className = "", type = "button" }) {
  const base = "inline-flex min-h-[56px] items-center justify-center gap-2 rounded-full px-8 text-base font-semibold transition-[background-color,transform,box-shadow,opacity] duration-200 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-45 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[rgba(156,130,184,.35)]";
  const v = variant === "primary"
    ? "bg-[var(--sb-plum)] text-white shadow-[0_14px_35px_rgba(74,21,75,0.25)] hover:bg-[var(--sb-plum-2)]"
    : "bg-white/70 text-[var(--sb-plum)] ring-1 ring-[var(--sb-lav-line)] hover:bg-white";
  return (
    <button type={type} onClick={onClick} disabled={disabled || busy} data-testid={testid} className={`${base} ${v} ${className}`}>
      {busy && <Loader2 className="h-5 w-5 animate-spin" />}{children}
    </button>
  );
}

export function Choice({ selected, onClick, children, testid, className = "" }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid} aria-pressed={selected}
      className={`sb-lift relative rounded-3xl border-2 bg-white text-left focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[rgba(156,130,184,.35)] ${selected ? "border-[var(--sb-plum)] bg-[var(--sb-lav-bg)]" : "border-transparent shadow-[0_10px_30px_-14px_rgba(74,21,75,0.18)]"} ${className}`}>
      {selected && <span className="absolute right-4 top-4 z-10 grid h-8 w-8 place-items-center rounded-full bg-[var(--sb-plum)] text-white"><Check className="h-4 w-4" /></span>}
      {children}
    </button>
  );
}

export function Chip({ selected, onClick, children, testid, disabled }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={testid} aria-pressed={selected}
      className={`min-h-[52px] rounded-full px-5 text-base font-medium transition-[background-color,color,box-shadow] duration-200 disabled:opacity-40 ${selected ? "bg-[var(--sb-plum)] text-white shadow-[0_8px_20px_rgba(74,21,75,.25)]" : "bg-white text-[var(--sb-ink)] ring-1 ring-[var(--sb-line)] hover:ring-[var(--sb-lav)]"}`}>
      {children}
    </button>
  );
}

export const Field = ({ label, error, children, hint }) => (
  <label className="block">
    <span className="mb-2 block text-sm font-semibold text-[var(--sb-muted)]">{label}</span>
    {children}
    {hint && !error && <span className="mt-2 block text-sm text-[var(--sb-muted)]">{hint}</span>}
    {error && <span className="mt-2 block text-sm font-medium text-[#B4232A]" role="alert">{error}</span>}
  </label>
);

export function Progress({ value }) {
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-[var(--sb-lav-line)]" data-testid="sb-progress" aria-label={`Progress ${Math.round(value * 100)}%`}>
      <div className="h-full rounded-full bg-[var(--sb-plum)] transition-[width] duration-500" style={{ width: `${Math.max(4, value * 100)}%` }} />
    </div>
  );
}

export function Nav({ onBack, onNext, nextLabel = "Continue", disabled, busy, testid = "sb-next" }) {
  return (
    <div className="mt-10 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
      {onBack ? <Btn variant="ghost" onClick={onBack} testid="sb-back">Previous</Btn> : <span />}
      {onNext && <Btn onClick={onNext} disabled={disabled} busy={busy} testid={testid}>{nextLabel}</Btn>}
    </div>
  );
}

export const PetAvatar = ({ pet, size = "h-16 w-16" }) => (
  pet?.photo
    ? <img src={pet.photo} alt="" className={`${size} shrink-0 rounded-2xl object-cover`} />
    : <span className={`${size} sb-serif grid shrink-0 place-items-center rounded-2xl bg-[var(--sb-lav-bg)] text-2xl font-semibold text-[var(--sb-plum)]`}>{(pet?.name || "?")[0]}</span>
);
